import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ArrowLeft } from "lucide-react";
import {
  NOTIFICATION_CATEGORY_LABEL,
  NOTIFICATION_KINDS,
  NOTIFICATION_KIND_LIST,
  alertsOnlyDelivery,
  deliveryFor,
  isAlertKind,
  notificationDeliverySchema,
  type NotificationCategory,
  type NotificationDelivery,
  type NotificationKind,
} from "@app/shared";
import { api, type SettingRow } from "@/lib/api";
import { queryKeys } from "@/lib/queryClient";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Skeleton } from "@/components/ui/skeleton";
import { pushToast } from "@/lib/toast";
import { PushDevices } from "@/components/PushDevices";

/**
 * M23c — which kinds are allowed out, and by which arm.
 *
 * Its own page rather than a card on Settings: thirty-two kinds times two arms does not
 * belong in a list beside "Dry run". Grouped by category, because the grouping is how a
 * debug session gets switched on and off — "tell me everything about trading tonight" is
 * one block, not ten switches.
 *
 * The stored value stays PARTIAL. Only kinds Andy has actually decided about are written,
 * so a kind added by a later build arrives with its default rather than with whatever this
 * page happened to render the day he last touched it.
 */

const SETTING_KEY = "notification_delivery";

const CATEGORY_ORDER: NotificationCategory[] = ["trading", "signals", "integrity", "system", "reports"];

export function NotificationPreferences() {
  const queryClient = useQueryClient();
  const query = useQuery({ queryKey: queryKeys.settings, queryFn: api.settings });

  const row = query.data?.settings.find((s: SettingRow) => s.key === SETTING_KEY);
  // A stored value this build cannot parse must not take the page down. Falling back to {}
  // shows every kind at its default, which is also what the server is doing with it.
  const parsed = notificationDeliverySchema.safeParse(row?.value ?? {});
  const prefs: NotificationDelivery = parsed.success ? parsed.data : {};

  const save = useMutation({
    mutationFn: ({ value }: { value: NotificationDelivery; toast?: string }) =>
      api.putSetting(SETTING_KEY, value),
    // Toasting in onSuccess rather than in the click handler, as every other setting does:
    // a preset that announces itself before the PUT lands would claim a change it may not
    // have made.
    onSuccess: (res, { toast }) => {
      if (toast) pushToast(toast);
      queryClient.setQueryData(queryKeys.settings, (old: { settings: SettingRow[] } | undefined) =>
        old
          ? { settings: old.settings.map((s) => (s.key === SETTING_KEY ? { ...s, value: res.value, stored: true } : s)) }
          : old,
      );
    },
  });

  const setKind = (kind: NotificationKind, arm: "telegram" | "push", value: boolean) => {
    const current = deliveryFor(prefs, kind);
    save.mutate({ value: { ...prefs, [kind]: { ...current, [arm]: value } } });
  };

  const setBlock = (kinds: NotificationKind[], arm: "telegram" | "push", value: boolean) => {
    const next = { ...prefs };
    for (const kind of kinds) next[kind] = { ...deliveryFor(prefs, kind), [arm]: value };
    save.mutate({ value: next });
  };

  if (query.isLoading) return <Skeleton className="h-64 w-full" />;
  if (query.isError) return <p className="text-sm text-destructive">Could not load settings.</p>;

  const alerting = NOTIFICATION_KIND_LIST.filter(isAlertKind).length;

  return (
    <div className="space-y-4">
      <div className="flex items-center gap-2">
        <Button asChild variant="ghost" size="sm">
          <Link to="/settings" aria-label="Back to settings">
            <ArrowLeft className="h-4 w-4" />
          </Link>
        </Button>
        <h1 className="text-lg font-semibold">What gets sent</h1>
      </div>

      <Card>
        <CardContent className="space-y-3 p-4">
          <p className="text-xs text-muted-foreground">
            Everything is always recorded and readable on the{" "}
            <Link to="/notifications" className="underline underline-offset-2">
              notifications page
            </Link>
            , whatever you switch off here. These control only what leaves the app.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button
              size="sm"
              variant="outline"
              disabled={save.isPending}
              onClick={() => save.mutate({ value: {}, toast: "Everything is sent again" })}
            >
              Send everything
            </Button>
            <Button
              size="sm"
              variant="outline"
              disabled={save.isPending}
              onClick={() =>
                save.mutate({
                  value: alertsOnlyDelivery(),
                  toast: `Only the ${alerting} kinds that mean something is wrong`,
                })
              }
            >
              Only when something's wrong
            </Button>
          </div>
        </CardContent>
      </Card>

      {CATEGORY_ORDER.map((category) => {
        const kinds = NOTIFICATION_KIND_LIST.filter((kind) => NOTIFICATION_KINDS[kind].category === category);
        const allTelegram = kinds.every((kind) => deliveryFor(prefs, kind).telegram);

        return (
          <Card key={category}>
            <CardHeader className="flex-row items-center justify-between gap-2 space-y-0">
              <CardTitle>{NOTIFICATION_CATEGORY_LABEL[category]}</CardTitle>
              <button
                type="button"
                disabled={save.isPending}
                onClick={() => setBlock(kinds, "telegram", !allTelegram)}
                // Five categories carry the same visible words; the accessible name says
                // which block this one acts on.
                aria-label={`${allTelegram ? "Mute" : "Send"} all in Telegram: ${NOTIFICATION_CATEGORY_LABEL[category]}`}
                className="text-xs text-muted-foreground underline-offset-2 hover:underline"
              >
                {allTelegram ? "Mute all in Telegram" : "Send all in Telegram"}
              </button>
            </CardHeader>
            <CardContent className="space-y-1">
              <div className="flex items-center justify-end gap-4 pb-1 text-[10px] uppercase tracking-wide text-muted-foreground">
                <span className="w-9 text-center">TG</span>
                <span className="w-9 text-center">Push</span>
              </div>
              {kinds.map((kind) => {
                const delivery = deliveryFor(prefs, kind);
                return (
                  <div key={kind} className="flex items-center justify-between gap-3 border-t py-2 first:border-t-0">
                    <div className="min-w-0">
                      <div className="truncate text-sm">{NOTIFICATION_KINDS[kind].label}</div>
                      {isAlertKind(kind) && (
                        <div className="text-[11px] text-muted-foreground">something is wrong</div>
                      )}
                    </div>
                    <div className="flex shrink-0 items-center gap-4">
                      <Switch
                        checked={delivery.telegram}
                        disabled={save.isPending}
                        aria-label={`Telegram: ${NOTIFICATION_KINDS[kind].label}`}
                        onCheckedChange={(v) => setKind(kind, "telegram", v)}
                      />
                      <Switch
                        checked={delivery.push}
                        disabled={save.isPending}
                        aria-label={`Push: ${NOTIFICATION_KINDS[kind].label}`}
                        onCheckedChange={(v) => setKind(kind, "push", v)}
                      />
                    </div>
                  </div>
                );
              })}
            </CardContent>
          </Card>
        );
      })}

      {/* M23d. The Push column is only meaningful once a device is enrolled, so the control
          that enrolls one sits on the same page rather than somewhere Andy has to find. */}
      <PushDevices />
    </div>
  );
}
